import type { Profile } from './profiles';

/**
 * PwC profile for advisory consultants running client ERP and HR transformation programmes.
 * Day is built around hands-on work in Oracle Fusion Cloud, Workday and SAP, with Microsoft 365 around it.
 */
export const pwcProfile: Profile = {
  name: 'Custom for PwC',
  averageSwitchingFrequencySeconds: 300,
  minMeetingsPerDay: 1,
  meetings: [
    {
      title: 'Vantrell ERP Programme - Daily Standup',
      durationMinutes: 30,
      entries: [
        { tool: 'microsoft_teams', title: 'Vantrell Finance Workstream Standup' },
        { tool: 'jira', title: 'VAN-Finance Sprint Board' },
        {
          tool: 'oracle',
          title: 'Oracle Fusion Cloud ERP | General Ledger: Period close checklist',
        },
        { tool: 'workday', title: 'Workday | Time Tracking: Workstream blockers by owner' },
        { tool: 'sap', title: 'SAP S/4HANA Fiori | Monitor Data Loads: Overnight run status' },
      ],
      aiNotes: {
        concise: 'Daily programme standup across the Vantrell finance and HR workstreams.',
        detailed:
          'Joining the daily standup with the Vantrell Industries finance workstream over Teams, walking the sprint board for open configuration and defect items, and checking where the Oracle General Ledger period close stands. The overnight S/4HANA data loads and the open Workday blockers were reviewed before the team committed to the day.',
      },
    },
    {
      title: 'Corvane Retail - Workday Time Tracking UAT Triage',
      durationMinutes: 60,
      entries: [
        { tool: 'microsoft_teams', title: 'Corvane UAT Triage Call' },
        { tool: 'workday', title: 'Workday | Time Tracking: UAT defect queue' },
        { tool: 'jira', title: 'COR-1184: Overtime rule misapplied for night shift' },
        {
          tool: 'sap',
          title: 'SAP SuccessFactors | Employee Central: Source record for defect COR-1184',
        },
        { tool: 'oracle', title: 'Oracle Fusion Cloud HCM | Payroll Interface: Rejected hours' },
        { tool: 'microsoft_excel', title: 'Corvane UAT Defect Log - Cycle 2.xlsx' },
        { tool: 'microsoft_teams', title: 'corvane-uat channel' },
      ],
      aiNotes: {
        concise: 'Triaging Workday Time Tracking defects raised in Corvane UAT cycle two.',
        detailed:
          'Running the user acceptance testing triage call with Corvane Retail Group, reproducing reported time tracking issues in Workday, and agreeing severity for each item. Priority went to an overtime rule misfiring on night shift schedules, traced back through the Employee Central source record and the rejected hours on the Oracle payroll interface.',
      },
    },
    {
      title: 'Vantrell Industries - Cutover Rehearsal Walkthrough',
      durationMinutes: 90,
      entries: [
        { tool: 'microsoft_teams', title: 'Cutover Rehearsal 2 - Walkthrough' },
        { tool: 'microsoft_powerpoint', title: 'Vantrell Cutover Approach.pptx' },
        { tool: 'microsoft_excel', title: 'Cutover Runbook - Rehearsal 2.xlsx' },
        { tool: 'sap', title: 'SAP S/4HANA Fiori | Data Migration Cockpit: Legacy load status' },
        { tool: 'oracle', title: 'Oracle Fusion Cloud ERP | FBDI: Journal import batch review' },
        { tool: 'workday', title: 'Workday | Integrations: Cutover freeze on worker exports' },
      ],
      aiNotes: {
        concise: 'Walking the Vantrell cutover runbook ahead of the second rehearsal.',
        detailed:
          'Stepping the client through the cutover runbook task by task, confirming owners and the cut-off times for legacy data extracts. Load status in the migration cockpit, the journal import batches and the freeze window on the Workday worker exports were reviewed live so the rehearsal can be sized properly.',
      },
    },
    {
      title: 'Solmark Energy Partners - Programme Steering Committee',
      durationMinutes: 60,
      entries: [
        { tool: 'microsoft_teams', title: 'Solmark Energy Steering Committee' },
        { tool: 'microsoft_powerpoint', title: 'Solmark Energy SteerCo - September.pptx' },
        { tool: 'microsoft_excel', title: 'Solmark Programme RAID Log.xlsx' },
        { tool: 'microsoft_sharepoint', title: 'Solmark Energy Programme Library' },
        { tool: 'sap', title: 'SAP SuccessFactors | Employee Central: Deployment readiness view' },
        { tool: 'workday', title: 'Workday | Projects: Programme burn by workstream' },
        { tool: 'oracle', title: 'Oracle Fusion Cloud ERP | Reports: Finance milestone status' },
      ],
      aiNotes: {
        concise:
          'Presenting programme status and open risks to the Solmark Energy steering committee.',
        detailed:
          'Taking the Solmark Energy Partners steering committee through progress on the HR and finance workstreams, budget consumption to date, and the open items on the RAID log. Readiness was shown live from SuccessFactors, Workday and Oracle rather than from slides, and the committee asked for a firmer date on the payroll parallel run.',
      },
    },
    {
      title: 'Corvane Retail - Ariba Supplier Onboarding Design',
      durationMinutes: 60,
      entries: [
        { tool: 'microsoft_teams', title: 'Supplier Onboarding Design Session' },
        { tool: 'sap', title: 'SAP Ariba | Supplier Lifecycle: Onboarding questionnaire draft' },
        { tool: 'oracle', title: 'Oracle Fusion Cloud ERP | Suppliers: Target supplier record' },
        { tool: 'workday', title: 'Workday | Financials: Supplier master field requirements' },
        { tool: 'microsoft_excel', title: 'Supplier Data Requirements - Corvane.xlsx' },
        { tool: 'microsoft_sharepoint', title: 'Corvane Procurement Design Documents' },
      ],
      aiNotes: {
        concise: 'Designing the Ariba supplier onboarding questionnaire with Corvane procurement.',
        detailed:
          'Working through the supplier onboarding flow in Ariba with the Corvane procurement team, deciding which questions gate registration versus qualification. Field-level requirements were mapped against the supplier records in Oracle and Workday so the integration team has something concrete to build to.',
      },
    },
    {
      title: '1:1 with Engagement Manager',
      durationMinutes: 30,
      entries: [
        { tool: 'microsoft_teams', title: '1:1 with Priya Raman' },
        { tool: 'microsoft_outlook', title: 'Notes: Staffing and utilisation discussion' },
        { tool: 'workday', title: 'Workday | Projects: Billable hours by engagement' },
        { tool: 'oracle', title: 'Oracle Fusion Cloud ERP | Projects: Engagement budget to date' },
        { tool: 'sap', title: 'SAP Concur | Expense: Outstanding reports awaiting approval' },
      ],
      aiNotes: {
        concise: 'Catching up with the engagement manager on staffing and billable hours.',
        detailed:
          'Weekly one-to-one covering current staffing across the Vantrell and Corvane engagements, billable utilisation for the month, and what a move onto the Solmark Energy workstream would mean. Engagement budget and the unapproved expense reports were cleared at the same time, with follow-ups captured in Outlook.',
      },
    },
  ],
  sessions: [
    {
      name: 'Oracle GL Period Close',
      predominantTool: 'oracle',
      entries: [
        {
          tool: 'oracle',
          title: 'Oracle Fusion Cloud ERP | General Ledger: Period close checklist',
        },
        { tool: 'oracle', title: 'Oracle Fusion Cloud ERP | General Ledger: Close monitor' },
        { tool: 'oracle', title: 'Oracle Fusion Cloud ERP | Reports: Trial balance by ledger' },
        { tool: 'microsoft_excel', title: 'Vantrell Close Variance Analysis.xlsx' },
        { tool: 'jira', title: 'VAN-762: Unposted subledger journals blocking close' },
      ],
      aiNotes: {
        concise: 'Working the Vantrell general ledger period close checklist in Oracle.',
        detailed:
          'Stepping through the close monitor in Oracle General Ledger, checking which subledgers have finished transferring and posting. Trial balance figures were pulled into a variance sheet, and the unposted journals blocking the close were logged for the client finance team.',
      },
    },
    {
      name: 'Chart of Accounts Mapping',
      predominantTool: 'oracle',
      entries: [
        { tool: 'oracle', title: 'Oracle Fusion Cloud ERP | Chart of Accounts: Segment values' },
        {
          tool: 'oracle',
          title: 'Oracle Fusion Cloud ERP | Chart of Accounts: Value set hierarchy',
        },
        { tool: 'microsoft_excel', title: 'Legacy to Oracle COA Mapping v11.xlsx' },
        { tool: 'microsoft_sharepoint', title: 'Vantrell Finance Design Documents' },
        { tool: 'jira', title: 'VAN-698: Cost centre hierarchy sign-off' },
      ],
      aiNotes: {
        concise: 'Mapping the legacy chart of accounts onto the Oracle segment structure.',
        detailed:
          'Reconciling legacy account codes against the new Oracle segment values, with particular attention to cost centres that split across two legal entities. The mapping workbook was updated and the hierarchy changes queued for client sign-off.',
      },
    },
    {
      name: 'Oracle HCM Data Migration Reconciliation',
      predominantTool: 'oracle',
      entries: [
        { tool: 'oracle', title: 'Oracle Fusion Cloud HCM | HDL: Worker load results' },
        { tool: 'oracle', title: 'Oracle Fusion Cloud HCM | Person Management: Spot checks' },
        { tool: 'microsoft_excel', title: 'Worker Migration Reconciliation - Load 6.xlsx' },
        { tool: 'jira', title: 'VAN-811: Assignment records rejected on HDL load' },
      ],
      aiNotes: {
        concise: 'Reconciling migrated worker records after the sixth Oracle HCM data load.',
        detailed:
          'Comparing loaded worker and assignment counts against the source extract, then spot checking individual records in Person Management. A batch of assignment rows failed on an effective date that predates the hire date, which is now logged for the migration team.',
      },
    },
    {
      name: 'Oracle UAT Defect Triage',
      predominantTool: 'oracle',
      maxLengthMinutes: 40,
      entries: [
        { tool: 'oracle', title: 'Oracle Fusion Cloud ERP | Payables: Invoice approval workflow' },
        { tool: 'oracle', title: 'Oracle Fusion Cloud ERP | Approvals: Rule configuration' },
        { tool: 'jira', title: 'VAN-844: Invoice approval skips second approver above threshold' },
        { tool: 'microsoft_teams', title: 'vantrell-uat channel' },
      ],
      aiNotes: {
        concise: 'Reproducing an Oracle Payables approval defect raised in user testing.',
        detailed:
          'Following the tester steps in Oracle Payables to reproduce an invoice that cleared without a second approver. The approval rule condition uses the wrong amount basis, so the fix sits with configuration rather than the workflow itself.',
      },
    },
    {
      name: 'Workday Timesheet and Project Billing',
      predominantTool: 'workday',
      maxLengthMinutes: 25,
      entries: [
        { tool: 'workday', title: 'Workday | Time Tracking: Week 37 timesheet' },
        { tool: 'workday', title: 'Workday | Projects: Billable time by engagement' },
        { tool: 'microsoft_outlook', title: 'Reminder: Submit timesheet before Friday cut-off' },
        { tool: 'jira', title: 'My engagement tasks - week 37' },
      ],
      aiNotes: {
        concise: 'Filling in the week 37 timesheet and splitting hours across engagements.',
        detailed:
          'Entering the week in Workday Time Tracking and allocating hours across the Vantrell, Corvane and Solmark Energy codes. Engagement task notes were used to reconstruct the split before submitting ahead of the Friday cut-off.',
      },
    },
    {
      name: 'Workday Business Process Configuration',
      predominantTool: 'workday',
      entries: [
        { tool: 'workday', title: 'Workday | HCM: Hire business process definition' },
        { tool: 'workday', title: 'Workday | HCM: Condition rules and security groups' },
        { tool: 'workday', title: 'Workday | Tenant: Configuration migration to sandbox' },
        { tool: 'microsoft_excel', title: 'Corvane Business Process Design Register.xlsx' },
        { tool: 'microsoft_sharepoint', title: 'Corvane HR Design Documents' },
      ],
      aiNotes: {
        concise: 'Configuring the Workday hire business process for Corvane Retail.',
        detailed:
          'Building out the hire business process steps in the Corvane tenant, adding condition rules so store hires route differently from head office roles. Changes were recorded in the design register and migrated into the sandbox for testing.',
      },
    },
    {
      name: 'Workday Financials Supplier Invoice Testing',
      predominantTool: 'workday',
      entries: [
        { tool: 'workday', title: 'Workday | Financials: Supplier invoice entry' },
        { tool: 'workday', title: 'Workday | Financials: Settlement run results' },
        { tool: 'microsoft_excel', title: 'Corvane Payables Test Script Results.xlsx' },
        { tool: 'jira', title: 'COR-1207: Withholding tax not applied on service invoices' },
      ],
      aiNotes: {
        concise: 'Running the Workday Financials supplier invoice test script for Corvane.',
        detailed:
          'Executing the payables test script end to end in Workday Financials, from invoice entry through to the settlement run. Service invoices came out without withholding tax, so the tax code assignment goes back to the finance configuration stream.',
      },
    },
    {
      name: 'Workday Integration Payload Review',
      predominantTool: 'workday',
      entries: [
        { tool: 'workday', title: 'Workday | Integrations: EIB worker export' },
        { tool: 'workday', title: 'Workday | Integrations: Event log and error report' },
        { tool: 'microsoft_excel', title: 'Workday to Payroll Field Mapping.xlsx' },
        { tool: 'jira', title: 'COR-1153: Payroll interface drops cost centre on transfers' },
        { tool: 'microsoft_teams', title: 'corvane-integrations channel' },
      ],
      aiNotes: {
        concise: 'Reviewing the Workday payroll integration output for dropped fields.',
        detailed:
          'Pulling the worker export and walking the integration event log to find where cost centre values disappear for transferred employees. The field mapping shows the value is sourced from the prior position, which explains the gap reported by payroll.',
      },
    },
    {
      name: 'SAP S/4HANA Close Monitoring',
      predominantTool: 'sap',
      entries: [
        { tool: 'sap', title: 'SAP S/4HANA Fiori | Financial Closing Cockpit: Task list' },
        { tool: 'sap', title: 'SAP S/4HANA Fiori | Manage Journal Entries: Period 9' },
        { tool: 'sap', title: 'SAP S/4HANA Fiori | Display Financial Statement: Solmark Energy' },
        { tool: 'microsoft_excel', title: 'Solmark Close Task Tracker.xlsx' },
      ],
      aiNotes: {
        concise: 'Monitoring the Solmark Energy period nine close in the S/4HANA closing cockpit.',
        detailed:
          'Watching the closing cockpit task list for period nine and chasing the two intercompany tasks that have not completed. Journal entries and the financial statement were reviewed for obvious postings errors before the client team signs off.',
      },
    },
    {
      name: 'SuccessFactors Employee Central Configuration',
      predominantTool: 'sap',
      entries: [
        { tool: 'sap', title: 'SAP SuccessFactors | Employee Central: Foundation objects' },
        { tool: 'sap', title: 'SAP SuccessFactors | Employee Central: Workflow configuration' },
        {
          tool: 'sap',
          title: 'SAP SuccessFactors | Manage Business Configuration: Job info fields',
        },
        { tool: 'microsoft_excel', title: 'Solmark Energy Position Structure.xlsx' },
        {
          tool: 'jira',
          title: 'SOL-427: Job Classification foundation object values for field roles',
        },
      ],
      aiNotes: {
        concise: 'Configuring Employee Central foundation objects for Solmark Energy field roles.',
        detailed:
          'Setting up job classifications and position structures in SuccessFactors Employee Central, with separate Job Classification foundation object values for offshore and onshore field roles. Workflow routing was adjusted so position changes reach the regional HR approver.',
      },
    },
    {
      name: 'Concur Expense Report',
      predominantTool: 'sap',
      maxLengthMinutes: 25,
      entries: [
        { tool: 'sap', title: 'SAP Concur | Expense Report: Client site travel, week 37' },
        { tool: 'sap', title: 'SAP Concur | Expense: Receipt matching and itemisation' },
        { tool: 'microsoft_outlook', title: 'Receipts: Rail fare and client site parking' },
        { tool: 'microsoft_teams', title: 'Question on billable travel policy' },
      ],
      aiNotes: {
        concise: 'Submitting the week 37 client travel expense report in Concur.',
        detailed:
          'Putting together the expense report for two days on the Vantrell client site, matching rail and parking receipts from email against the card feed. One meal needed itemising before the report would pass policy validation.',
      },
    },
    {
      name: 'Ariba Supplier Onboarding Workstream',
      predominantTool: 'sap',
      entries: [
        { tool: 'sap', title: 'SAP Ariba | Supplier Lifecycle: Registration queue' },
        { tool: 'sap', title: 'SAP Ariba | Sourcing: Supplier qualification template' },
        { tool: 'microsoft_excel', title: 'Corvane Supplier Master Cleanse.xlsx' },
        { tool: 'jira', title: 'COR-1176: Duplicate supplier records blocking registration' },
      ],
      aiNotes: {
        concise: 'Working the Ariba supplier registration queue and qualification template.',
        detailed:
          'Clearing the supplier registration queue in Ariba and updating the qualification template with the new risk questions. Several registrations are stuck behind duplicate master records, so the cleanse workbook was updated for the data team.',
      },
    },
    {
      name: 'Steering Committee Deck Preparation',
      predominantTool: 'microsoft_powerpoint',
      entries: [
        { tool: 'microsoft_powerpoint', title: 'Solmark Energy SteerCo - September.pptx' },
        { tool: 'microsoft_powerpoint', title: 'Programme Milestone Timeline.pptx' },
        { tool: 'microsoft_excel', title: 'Solmark Programme RAID Log.xlsx' },
        { tool: 'microsoft_excel', title: 'Budget Burn vs Plan - Solmark.xlsx' },
        { tool: 'microsoft_sharepoint', title: 'Solmark Energy Programme Library' },
      ],
      aiNotes: {
        concise: 'Building the September steering committee deck for Solmark Energy.',
        detailed:
          'Pulling the milestone timeline and budget burn figures into the steering committee deck, and rewriting the risk slide around the payroll parallel run. The final version was filed in the programme library ahead of the meeting.',
      },
    },
    {
      name: 'Weekly Client Status Report',
      predominantTool: 'microsoft_word',
      entries: [
        { tool: 'microsoft_word', title: 'Vantrell Weekly Status Report - Week 37.docx' },
        { tool: 'microsoft_excel', title: 'Workstream Progress Tracker.xlsx' },
        { tool: 'jira', title: 'VAN-Finance Sprint Board' },
        { tool: 'microsoft_outlook', title: 'Draft: Weekly status to Vantrell programme office' },
      ],
      aiNotes: {
        concise: 'Writing the weekly status report for the Vantrell programme office.',
        detailed:
          'Summarising the week across the finance and HR workstreams, with completion percentages taken from the progress tracker and the sprint board. Two items moved to amber on the back of the data migration rejections found earlier.',
      },
    },
    {
      name: 'Cutover Plan and Runbook',
      predominantTool: 'microsoft_excel',
      entries: [
        { tool: 'microsoft_excel', title: 'Cutover Runbook - Rehearsal 2.xlsx' },
        { tool: 'microsoft_excel', title: 'Cutover Task Dependencies.xlsx' },
        { tool: 'sap', title: 'SAP S/4HANA Fiori | Data Migration Cockpit: Legacy load status' },
        { tool: 'oracle', title: 'Oracle Fusion Cloud ERP | FBDI: Journal import batch review' },
        { tool: 'microsoft_sharepoint', title: 'Vantrell Cutover Workspace' },
      ],
      aiNotes: {
        concise: 'Sequencing cutover tasks and timings for the Vantrell rehearsal.',
        detailed:
          'Reworking the runbook so the data loads and the finance validation tasks no longer overlap, then re-timing the critical path. The revised sequence pushes the go or no-go decision point two hours later than planned.',
      },
    },
    {
      name: 'Programme Documentation',
      predominantTool: 'microsoft_sharepoint',
      entries: [
        { tool: 'microsoft_sharepoint', title: 'Vantrell Finance Design Documents' },
        { tool: 'microsoft_sharepoint', title: 'Corvane HR Design Documents' },
        { tool: 'microsoft_word', title: 'Functional Design - Payables Approvals.docx' },
        { tool: 'microsoft_word', title: 'Test Strategy - UAT Cycle 3.docx' },
        { tool: 'microsoft_excel', title: 'Design Decision Register.xlsx' },
      ],
      aiNotes: {
        concise: 'Updating functional design documents and the decision register.',
        detailed:
          'Revising the payables approvals design to match what was agreed in triage, and drafting the entry criteria for the third user acceptance testing cycle. Each change was logged in the decision register with the approver named.',
      },
    },
    {
      name: 'Email and Programme Correspondence',
      predominantTool: 'microsoft_outlook',
      maxLengthMinutes: 30,
      entries: [
        { tool: 'microsoft_outlook', title: 'Inbox: Vantrell programme office' },
        { tool: 'microsoft_outlook', title: 'Thread: Corvane UAT cycle 3 entry criteria' },
        { tool: 'microsoft_outlook', title: 'Calendar: Next week client site days' },
        { tool: 'microsoft_teams', title: 'Unread channel mentions' },
      ],
      aiNotes: {
        concise: 'Clearing programme correspondence and confirming next week client site days.',
        detailed:
          'Working through the inbox from the client programme office, replying on the testing entry criteria thread, and blocking out the travel days for next week. Outstanding Teams mentions were picked up at the end.',
      },
    },
    {
      name: 'Team Coordination',
      predominantTool: 'microsoft_teams',
      maxLengthMinutes: 30,
      entries: [
        { tool: 'microsoft_teams', title: 'vantrell-finance channel' },
        { tool: 'microsoft_teams', title: 'corvane-integrations channel' },
        { tool: 'microsoft_teams', title: 'Tomas Lindqvist (chat)' },
        { tool: 'jira', title: 'Engagement backlog - unassigned items' },
      ],
      aiNotes: {
        concise: 'Coordinating with workstream colleagues across the client channels.',
        detailed:
          'Answering questions in the finance and integrations channels, and agreeing with a colleague who picks up the remaining unassigned backlog items. Two questions needed a decision from the client before anyone could move.',
      },
    },
    {
      name: 'Product Research and Reference Documentation',
      predominantTool: 'google_chrome',
      maxLengthMinutes: 35,
      entries: [
        { url: 'https://docs.oracle.com', title: 'Oracle Help Center - Fusion Cloud ERP' },
        { url: 'https://help.sap.com', title: 'SAP Help Portal - S/4HANA Financial Closing' },
        { tool: 'google_chrome', title: 'Workday business process condition rule examples' },
        { tool: 'microsoft_word', title: 'Research Notes - Close Automation Options.docx' },
      ],
      aiNotes: {
        concise: 'Reading vendor documentation on close automation and condition rules.',
        detailed:
          'Checking the Oracle and SAP product documentation for what the standard close automation actually covers, so the client is not sold a custom build they do not need. Findings were written up as notes for the finance workstream lead.',
      },
    },
  ],
};
